import React, { createContext, useContext, useEffect } from 'react';
import { useMotionValue, useSpring } from 'framer-motion';

const MouseContext = createContext(null);

export const MouseProvider = ({ children }) => {
  // Absolute screen coordinates
  const mouseX = useMotionValue(typeof window !== 'undefined' ? window.innerWidth / 2 : 0);
  const mouseY = useMotionValue(typeof window !== 'undefined' ? window.innerHeight / 2 : 0);

  // Normalized coordinates: -1 to 1 (0 is center)
  const normX = useMotionValue(0);
  const normY = useMotionValue(0);

  // Smooth springs for 120FPS fluidity
  const smoothMouseX = useSpring(mouseX, { stiffness: 50, damping: 20, mass: 0.5 });
  const smoothMouseY = useSpring(mouseY, { stiffness: 50, damping: 20, mass: 0.5 });
  const smoothNormX = useSpring(normX, { stiffness: 50, damping: 20, mass: 0.5 });
  const smoothNormY = useSpring(normY, { stiffness: 50, damping: 20, mass: 0.5 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      
      mouseX.set(clientX);
      mouseY.set(clientY);
      
      normX.set((clientX / innerWidth) * 2 - 1);
      normY.set((clientY / innerHeight) * 2 - 1);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY, normX, normY]);

  return (
    <MouseContext.Provider
      value={{
        mouseX,
        mouseY,
        normX,
        normY,
        smoothMouseX,
        smoothMouseY,
        smoothNormX,
        smoothNormY,
      }}
    >
      {children}
    </MouseContext.Provider>
  );
};

export const useMouseContext = () => {
  const context = useContext(MouseContext);
  if (!context) {
    throw new Error('useMouseContext must be used within a MouseProvider');
  }
  return context;
};
